import { Close } from '@mui/icons-material';
import { Button, Modal, TextField } from '@mui/material';
import { captureException } from '@sentry/react';
import Acent from 'components/Acent';
import useStateManager from 'components/hooks/useStateManager';
import Loading from 'components/Loading';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import React, { useEffect, useRef } from 'react';
import { useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import { useQuotes } from 'utils/methods/quotes';

const AddEditQuotes = ({ open, onClose, quotesData, id, edit }) => {
  const showAlert = useAlert();
  const { createQuotes, updateQuotes } = useQuotes();
  const currentUser = useCurrentUser();
  const authorTextRef = useRef();
  const quoteTextRef = useRef();
  const [states, upateStates] = useStateManager({
    quoteText: edit?.editData?.message || '',
    authorText: edit?.editData?.author || '',
    loading: { loader: false, value: false },
  });

  useEffect(() => {
    if (edit?.editData) {
      upateStates({ quoteText: edit?.editData?.message, authorText: edit?.editData?.author });
    }
  }, [edit]);

  const handlerSave = async () => {
    try {
      if (!states?.quoteText || states.quoteText.trim()?.length === 0) {
        showAlert('Quote is required', 'error');
        return;
      } else if (!states?.authorText || states.authorText.trim()?.length === 0) {
        showAlert('Author is required', 'error');
        return;
      }
      upateStates({ loading: { value: 'add', loader: true } });

      const newQuote = {
        message: states.quoteText.trim(),
        author: states.authorText.trim(),
        created_at: new Date().toISOString(),
        id: Date.now().toString(),
      };
      const updatedData = [...quotesData, newQuote];
      const payload = {
        id: id,
        created_at: new Date(),
        updated_at: new Date(),
        user: {
          role: currentUser.role,
          id: currentUser.id,
        },
        data: JSON.stringify(updatedData),
      };

      await createQuotes(payload, id);
      onClose();
    } catch (error) {
      captureException(`659 Error listening to session changes: ${error}`);
    } finally {
      upateStates({ loading: { value: '', loader: false, quoteText: '', authorText: '' } });
    }
  };

  const handlerUpdate = async (quoteIndex, updatedQuote) => {
    try {
      if (quoteIndex === null || quoteIndex === undefined) {
        return;
      }
      upateStates({ loading: { value: 'update', loader: true } });
      const updatedData = [...quotesData];
      updatedData[quoteIndex] = {
        ...updatedData[quoteIndex],
        message: updatedQuote.message,
        author: updatedQuote.author,
        updated_at: new Date().toISOString(),
      };

      const payload = {
        data: JSON.stringify(updatedData),
        updated_at: new Date(),
        user: {
          role: currentUser.role,
          id: currentUser.id,
        },
      };

      // Update in Firestore
      const result = await updateQuotes(id, payload);

      if (result.success) {
        showAlert('Quote updated successfully', 'success');
        onClose();
      } else {
        showAlert('Failed to update quote', 'success');
      }
    } catch (error) {
      captureException(`659 Error listening to session changes: ${error}`);
    } finally {
      upateStates({ loading: { value: 'update', loader: false } });
    }
  };

  const handleSubmit = () => {
    if (edit?.isEdit) {
      handlerUpdate(edit.editIndex, { message: states.quoteText, author: states.authorText });
      return;
    }
    handlerSave();
  };

  return (
    <>
      {open && (
        <Modal open={open} onClose={onClose} className="modal-for-rubric">
          <ModalContent orientation={window.screen.orientation}>
            <div className="flex items-center w-full justify-between">
              <SelectRubricText className="text-center">
                {' '}
                {edit?.isEdit ? 'Edit' : 'Add'} Quote
              </SelectRubricText>
              <CloseButton>
                <Close
                  className="cursor-pointer"
                  onClick={() => {
                    onClose();
                  }}
                />
              </CloseButton>
            </div>
            <div className="mt-10px">
              <Acent
                inputRef={authorTextRef}
                setValue={(value) => upateStates({ authorText: value })}
                style={{ marginBottom: 5, display: 'flex', justifyContent: 'flex-end' }}
              />
              <TextField
                id="displayName"
                name="Quote"
                label="Author Name"
                InputLabelProps={{ shrink: true, required: true }}
                type="text"
                required
                inputRef={authorTextRef}
                variant="outlined"
                onChange={(e) => {
                  upateStates({ authorText: e.target.value });
                }}
                value={states?.authorText}
                className="quote-display-name"
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '5px',
                  },
                }}
              />
            </div>

            <div className="mt-10px">
              <Acent
                inputRef={quoteTextRef}
                setValue={(value) => upateStates({ quoteText: value })}
                style={{ marginBottom: 5, display: 'flex', justifyContent: 'flex-end' }}
                className="wrapper-acent-quote"
              />
              <TextField
                id="displayName"
                name="Quote"
                label="Quote"
                InputLabelProps={{ shrink: true, required: true }}
                type="text"
                required
                inputRef={quoteTextRef}
                variant="outlined"
                multiline
                rows={4}
                onChange={(e) => {
                  upateStates({ quoteText: e.target.value });
                }}
                value={states?.quoteText}
              />
            </div>
            <div className="flex items-center gap-10px mt-10px w-full justify-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  (states?.loading?.loader && states?.loading?.value === 'add') ||
                  (states?.loading?.loader && states?.loading?.value === 'update')
                }
              >
                {(states?.loading?.loader && states?.loading?.value === 'add') ||
                (states?.loading?.loader && states?.loading?.value === 'update') ? (
                  <Loading white={true} size={18} />
                ) : null}
                {edit?.isEdit ? 'Update' : 'Save'}
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default AddEditQuotes;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding-bottom: 24px;

  padding-top: 20px;

  position: relative;
  width: ${(props) => (props.orientation === 'landscape' ? '90%' : '100%')};
  height: fit-content;
  box-sizing: border-box;

  max-width: 500px;
  padding: 1.5rem 1.5rem;
  max-height: 600px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 425px) {
    padding: 10px;
    margin-inline: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgb(204, 80, 71);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
`;

const SelectRubricText = styled.div`
  color: rgb(32, 33, 36);
  font-family: 'Google Sans', Roboto, Arial, sans-serif;
  line-height: 1.75rem;
  font-size: 1.375rem;
  letter-spacing: 0;
  font-weight: 400;
`;
